@use '../../design-tokens/typography/typography' as *;
@use '../../design-tokens/border-radius/border-radius' as *;
@use '../../design-tokens/decorative-color/decorative-color' as *;

// Unlayered so kind colors beat Chip/Badge `@layer design-system` defaults.
@mixin pill-chrome {
  @include typography-v2-body-xs;

  padding: 3px 8px;
  gap: 4px;
  border-radius: 9999px;
}

.interactive {
  @include pill-chrome;

  // Keep in sync with `.readOnly` — same baseline-drift issue applies here.
  vertical-align: middle;

  // Own stacking context so `::before` (z-index: -1) stays behind this chip.
  position: relative;
  z-index: 0;

  // Fixed box size; hover grow is on `::before` so the editor doesn't bounce.
  padding: 4px 8px;
  border: none;

  &::before {
    content: '';
    position: absolute;
    inset: 1px 0;
    z-index: -1;
    border-radius: 9999px;
    outline: 1px solid transparent;
    outline-offset: -1px;
  }

  &:hover::before {
    inset: 0;
  }

  &.entity {
    @include decorative-pill-color('amber');
    @include decorative-pill-focus('amber');
  }

  &.theme {
    @include decorative-pill-color('indigo');
    @include decorative-pill-focus('indigo');
  }

  &.watchlist {
    color: var(--text-default);

    &::before {
      background-color: var(--surface-soft);
      outline-color: var(--border-soft);
    }

    &:hover::before {
      background-color: var(--surface-hover);
      outline-color: var(--border-hover);
    }

    &:focus-visible {
      outline: 2px solid var(--border-focus);
    }
  }

  &.insight {
    @include decorative-pill-color('teal');
    @include decorative-pill-focus('teal');
  }

  &.insightScenario {
    @include decorative-pill-color('indigo');
    @include decorative-pill-focus('indigo');
  }

  &.insightCatalystCompany {
    @include decorative-pill-color('lime');
    @include decorative-pill-focus('lime');
  }

  &.insightCatalystMarket {
    @include decorative-pill-color('cyan');
    @include decorative-pill-focus('cyan');
  }

  &.insightEarningsPreview {
    @include decorative-pill-color('purple');
    @include decorative-pill-focus('purple');
  }

  &.insightEarningsRecap {
    @include decorative-pill-color('teal');
    @include decorative-pill-focus('teal');
  }

  &.selected {
    // box-shadow stacks with `::before` outline; a second outline would replace it.
    box-shadow: 0 0 0 2px currentColor;
  }
}

.readOnly {
  // Default inline `vertical-align: baseline` aligns an `<img>` avatar's
  // bottom edge to the text baseline, but aligns text/SVG glyph chips by
  // their own baseline — same line, different anchor, so chips visibly
  // drift relative to each other. Middle-align every chip consistently.
  vertical-align: middle;

  // The pill (with its glyph/padding/border) is taller than the
  // surrounding text's line-height, so wrapped lines that both contain
  // chips end up with ~0px gap between them and look like they overlap.
  // Vertical margin grows the line box enough to keep lines apart.
  margin: 2px 0;

  [data-badge='textual'] {
    @include pill-chrome;

    border: 1px solid transparent;
  }

  &.entity [data-badge='textual'] {
    @include decorative-badge-color('amber');
  }

  &.theme [data-badge='textual'] {
    @include decorative-badge-color('indigo');
  }

  &.watchlist [data-badge='textual'] {
    background-color: var(--surface-soft);
    border-color: var(--border-soft);
    color: var(--text-default);

    &:hover {
      background-color: var(--surface-hover);
      border-color: var(--border-hover);
    }
  }

  &.insight [data-badge='textual'] {
    @include decorative-badge-color('teal');
  }

  &.insightScenario [data-badge='textual'] {
    @include decorative-badge-color('indigo');
  }

  &.insightCatalystCompany [data-badge='textual'] {
    @include decorative-badge-color('lime');
  }

  &.insightCatalystMarket [data-badge='textual'] {
    @include decorative-badge-color('cyan');
  }

  &.insightEarningsPreview [data-badge='textual'] {
    @include decorative-badge-color('purple');
  }

  &.insightEarningsRecap [data-badge='textual'] {
    @include decorative-badge-color('teal');
  }

  svg {
    fill: currentColor;
  }
}

// Prefer `surface-default` over `surface-inverse` so the letter stays visible in dark theme.
.entityInitial {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  @include border-radius-full;
  background-color: var(--surface-default);
  color: var(--text-default);
  @include typography-v2-body-xxs;

  font-weight: 500;
  font-style: normal;
  line-height: 1;
  text-transform: uppercase;

  &::before {
    content: attr(data-initial);
  }
}

.watchlistGlyphWrap {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  @include border-radius-full;
  background-color: var(--surface-active);
}

.watchlistGlyph {
  fill: currentColor;
}

.label {
  display: inline-block;
  max-width: 240px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  vertical-align: bottom;
}
